import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

import { ROUTES, KIPIVERSE_INQUIRY_STATUS } from '@/utils/constants';
import {
  useGetKipiverseInquiryById,
  useUpdateKipiverseInquiryStatus,
} from './hooks/useKipiverseInquiries';
import {
  updateKipiverseInquiryStatusSchema,
  UpdateKipiverseInquiryStatusFormValues,
} from './schema';

export default function EditKipiverseInquiryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: inquiryData, isLoading } = useGetKipiverseInquiryById(id as string);
  const { mutate: updateStatus, isPending } = useUpdateKipiverseInquiryStatus();

  const form = useForm<UpdateKipiverseInquiryStatusFormValues>({
    resolver: zodResolver(updateKipiverseInquiryStatusSchema),
    defaultValues: {
      status: KIPIVERSE_INQUIRY_STATUS.PENDING,
    },
  });

  useEffect(() => {
    if (inquiryData?.data) {
      form.reset({
        status: inquiryData.data.status,
      });
    }
  }, [inquiryData, form]);

  const onSubmit = (values: UpdateKipiverseInquiryStatusFormValues) => {
    if (!id) return;
    updateStatus(
      { id, data: values },
      {
        onSuccess: () => {
          toast.success('Inquiry status updated successfully.');
          navigate(ROUTES.KIPIVERSE_INQUIRYS);
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Failed to update status.');
        },
      },
    );
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const inquiry = inquiryData?.data;

  return (
    <div className="w-full py-6 space-y-6  max-w-4xl mx-auto pb-24 animate-fade-in bg-background min-h-screen">
      <PageHeader title="Edit Inquiry Status" subtitle="Update the status of the inquiry.">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto mt-4 sm:mt-0">
          <Button
            variant="outline"
            className="w-full sm:w-auto justify-center"
            onClick={() => navigate(ROUTES.KIPIVERSE_INQUIRYS)}
          >
            Back
          </Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader className="px-4 sm:px-6">
          <CardTitle>Inquiry Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4  pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={inquiry?.fullName || ''} readOnly disabled />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={inquiry?.email || ''} readOnly disabled />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={inquiry?.phone || ''} readOnly disabled />
            </div>
            <div className="space-y-2">
              <Label>Organization</Label>
              <Input value={inquiry?.organization || ''} readOnly disabled />
            </div>
            <div className="space-y-2">
              <Label>Interested In</Label>
              <Input value={inquiry?.interestedIn || ''} readOnly disabled />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea value={inquiry?.message || ''} readOnly disabled className="resize-none" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="px-4 sm:px-6">
          <CardTitle>Update Status</CardTitle>
        </CardHeader>
        <CardContent className=" pt-0">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(KIPIVERSE_INQUIRY_STATUS).map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8 pt-6 border-t border-border w-full">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(ROUTES.KIPIVERSE_INQUIRYS)}
                  disabled={isPending}
                  className="w-full sm:w-auto rounded-xl px-8 h-12 border-border font-bold uppercase tracking-widest text-xs hover:bg-accent transition-colors order-2 sm:order-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isPending}
                  className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-all shadow-md h-12 px-8 uppercase tracking-widest text-xs justify-center order-1 sm:order-2"
                >
                  {isPending ? 'Updating...' : 'Update Status'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
